/**
 * Plan Utilities - Helper functions for plan data manipulation
 */

import { Plan } from '../models/plan';
import type { PlanMeter } from '../types/plan.types';

/**
 * Every metered limit a plan carries, in display order.
 * Mirrors backend LIMIT_METRICS (field name = `${meter}_limit`).
 */
export const PLAN_METERS: ReadonlyArray<{ key: PlanMeter; label: string; short: string }> = [
  { key: 'cv', label: 'CV Limit', short: 'CV' },
  { key: 'interview', label: 'Interview Limit', short: 'Int' },
  { key: 'job', label: 'Jobs Limit', short: 'Jobs' },
  { key: 'talent_search', label: 'Talent Cloud Searches', short: 'Search' },
  { key: 'talent_unlock', label: 'Talent Cloud Unlocks', short: 'Unlock' },
];

/** Read a plan's limit for a given meter. */
export const getPlanMeterLimit = (plan: Plan, meter: PlanMeter): number | null =>
  plan[`${meter}_limit`];

/**
 * Format limit value for display
 * -1 or null → "∞" (unlimited)
 * Otherwise → number as string
 */
export const formatLimit = (limit: number | null | undefined): string => {
  if (limit === null || limit === undefined || limit === -1) {
    return '∞';
  }
  return limit.toString();
};

/**
 * Format plan duration for display
 */
export const formatDuration = (durationDays: number | null | undefined): string => {
  if (!durationDays) return '';
  return ` • Duration: ${durationDays} days`;
};

/**
 * Get plan display text for dropdown options
 */
export const getPlanDisplayText = (plan: Plan): string => {
  const limits = PLAN_METERS.map(
    (m) => `${m.short}: ${formatLimit(getPlanMeterLimit(plan, m.key))}`
  ).join(', ');
  const durationInfo = formatDuration(plan.duration_days);

  return `${plan.name} • ${limits}${durationInfo}`;
};

/**
 * Filter plans by kind
 */
export const filterPlansByKind = (plans: Plan[], kind: Plan['plan_kind']): Plan[] => {
  return plans.filter(plan => plan.plan_kind === kind);
};

/**
 * Find plan by code
 */
export const findPlanByCode = (plans: Plan[], planCode: string): Plan | undefined => {
  return plans.find(plan => plan.plan_code === planCode);
};

